// fetchWithAuthRetry — gives raw `fetch()` call sites the same
// refresh-once-on-401-and-retry behavior that attachAuthResponseInterceptor
// (axios-auth.ts) gives axios-based requests.
//
// Several hooks (useWorkOrderVersions, useEstimateDocuments, etc.) predate
// the shared axios client and call fetch() directly with a hand-rolled
// Authorization header. Those requests never pass through the axios
// interceptor, so once the 30-minute access token expires mid-session they
// fail with a permanent 401 (Save Draft / Generate Work Order and Share)
// even though the rest of the app silently refreshes and keeps working.
//
// This wraps fetch with the same single-flight refreshAccessToken() used by
// the axios interceptor, retrying the request exactly once with the new
// token. Super-admin tokens are left alone (no refresh path), matching
// attachAuthResponseInterceptor's behavior.

import { refreshAccessToken, RefreshUnavailableError } from './axios-auth';

function isSuperAdmin(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('user_type') === 'super_admin';
}

export async function fetchWithAuthRetry(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status !== 401 || isSuperAdmin()) return res;

  let newToken: string | null;
  try {
    newToken = await refreshAccessToken();
  } catch (err) {
    if (err instanceof RefreshUnavailableError) {
      // Couldn't even attempt the refresh (network/transient) -- surface
      // the original 401 rather than retrying with a token we never got;
      // the caller's own error handling takes it from here, same as
      // before this function existed.
      return res;
    }
    throw err;
  }
  if (!newToken) return res;

  const retryHeaders = new Headers(init?.headers);
  retryHeaders.set('Authorization', `Bearer ${newToken}`);
  return fetch(input, { ...init, headers: retryHeaders });
}
